import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';

interface SearchResult {
  type: 'tutorial' | 'product' | 'workshop' | 'file';
  id: string;
  title: string;
  description: string;
  price?: number;
  discount?: number;
  category: string;
  imagePath: string;
  priority: number;
  createdDate: string;
}

const SearchCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const SearchCardMedia = styled(CardMedia)(() => ({
  height: 200,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const TypeChip = styled(Chip)<{ type: string; isRTL?: boolean }>(({ theme, type, isRTL }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  [isRTL ? 'left' : 'right']: theme.spacing(1),
  backgroundColor: type === 'tutorial' ? '#4caf50' : 
                   type === 'file' ? '#2196f3' : 
                   type === 'product' ? '#ff9800' : '#9c27b0',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
}));

const PriceBox = styled(Box)<{ isRTL?: boolean }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
}));

const DiscountedPrice = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '1.1rem',
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
}));

export const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = searchParams.get('q') || '';

  useEffect(() => {
    const performSearch = async () => {
      if (!query.trim()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/search?q=${encodeURIComponent(query)}`);
        
        if (response.data.success) {
          setResults(response.data.data);
        } else {
          setError(response.data.message || 'Search failed');
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to perform search. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query]);

  const handleItemClick = (item: SearchResult) => {
    switch (item.type) {
      case 'tutorial':
        navigate(`/category/${item.category}`);
        break;
      case 'product':
        navigate(`/category/${item.category}`);
        break;
      case 'workshop':
        navigate(`/category/${item.category}`);
        break;
      case 'file':
        navigate(`/category/${item.category}`);
        break;
      default:
        break;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'tutorial':
        return t('search.types.tutorial');
      case 'file':
        return t('search.types.file');
      case 'product':
        return t('search.types.product');
      case 'workshop':
        return t('search.types.workshop');
      default:
        return type;
    }
  };

  const getCategoryLabel = (category: string) => {
    return t(`header.nav.${category}`) || category;
  };

  const formatPrice = (price: number, discount?: number) => {
    if (discount && discount > 0) {
      const discountedPrice = price * (1 - discount / 100);
      return (
        <PriceBox isRTL={currentLang === 'he'}>
          <OriginalPrice>₪{price.toFixed(2)}</OriginalPrice>
          <DiscountedPrice>₪{discountedPrice.toFixed(2)}</DiscountedPrice>
        </PriceBox>
      );
    }
    return (
      <PriceBox isRTL={currentLang === 'he'}>
        <DiscountedPrice>₪{price.toFixed(2)}</DiscountedPrice>
      </PriceBox>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('search.backToHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2} sx={{ 
          textAlign: currentLang === 'he' ? 'right' : 'left'
        }}>
          <SearchIcon color="primary" />
          <Typography variant="h4" component="h1" sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'left',
            direction: currentLang === 'he' ? 'rtl' : 'ltr'
          }}>
            {t('search.resultsFor')} "{query}"
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ 
          textAlign: currentLang === 'he' ? 'right' : 'left',
          direction: currentLang === 'he' ? 'rtl' : 'ltr'
        }}>
          {results.length} {t('search.resultsFound')}
        </Typography>
      </Paper>

      {results.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'center',
            direction: currentLang === 'he' ? 'rtl' : 'ltr'
          }}>
            {t('search.noResults')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            mb: 3,
            textAlign: currentLang === 'he' ? 'right' : 'center',
            direction: currentLang === 'he' ? 'rtl' : 'ltr'
          }}>
            {t('search.tryDifferentKeywords')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            {t('search.backToHome')}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {results.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={`${item.type}-${item.id}`}>
              <SearchCard onClick={() => handleItemClick(item)}>
                <Box position="relative">
                  <SearchCardMedia
                    image={item.imagePath}
                    title={item.title}
                  />
                  <TypeChip
                    label={getTypeLabel(item.type)}
                    size="small"
                    type={item.type}
                    isRTL={currentLang === 'he'}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" component="h2" gutterBottom sx={{ 
                    textAlign: currentLang === 'he' ? 'right' : 'left',
                    direction: currentLang === 'he' ? 'rtl' : 'ltr'
                  }}>
                    {item.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      flexGrow: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textAlign: currentLang === 'he' ? 'right' : 'left',
                      direction: currentLang === 'he' ? 'rtl' : 'ltr'
                    }}
                  >
                    {item.description}
                  </Typography>
                  
                  {item.price !== undefined && formatPrice(item.price, item.discount)}
                  
                  <CategoryChip
                    label={getCategoryLabel(item.category)}
                    size="small"
                    variant="outlined"
                  />
                </CardContent>
              </SearchCard>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};
